import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, TokenResponse } from '@/types';
import { authService, normalizeTokens } from '@/services/authService';
import { setTokens, clearTokens } from './tokenAccess';

interface AuthState {
  user: User | null;
  tokens: TokenResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string>;
  setUser: (user: User) => void;
  setTokens: (tokens: TokenResponse) => void;
  clearError: () => void;
  initializeAuth: () => Promise<void>;
}

const TOKEN_KEY = 'fitness-auth-storage';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const tokens = await authService.login({ email, password });
          // Сначала сохраняем токены, и только потом запрашиваем /me: api-клиент
          // (api.ts interceptor) читает access_token из tokenAccess и шлёт как Bearer.
          // Явно прокидываем в tokenAccess ВРУЧНУЮ (помимо subscribe) — это гарантия,
          // не зависящая от порядка persist-hydration / ESM-инициализации: без неё
          // /me уходил без Authorization → 302/401 → "Network error".
          set({ tokens });
          setTokens(tokens);
          const user = await authService.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      register: async (email: string, password: string, confirmPassword: string) => {
        set({ isLoading: true, error: null });
        try {
          await authService.register({ email, password, confirmPassword });
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        const { tokens } = get();
        if (tokens?.refreshToken) {
          try {
            await authService.logout(tokens.refreshToken);
          } catch {
            // Ignore logout errors
          }
        }
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          error: null,
        });
        clearTokens();
      },

      refreshToken: async () => {
        const { tokens } = get();
        if (!tokens?.refreshToken) throw new Error('No refresh token');

        const newTokens = await authService.refreshToken(tokens.refreshToken);
        set({ tokens: newTokens });
        setTokens(newTokens);
        return newTokens.accessToken;
      },

      setUser: (user: User) => set({ user }),

      setTokens: (tokens: TokenResponse) => {
        set({ tokens, isAuthenticated: true });
        setTokens(tokens);
      },

      clearError: () => set({ error: null }),

      initializeAuth: async () => {
        const { tokens } = get();
        if (!tokens?.accessToken) return;

        // При восстановлении из localStorage (перезагрузка страницы) прокидываем
        // токены в tokenAccess, чтобы interceptor шёл с Authorization.
        setTokens(tokens);

        try {
          const user = await authService.getCurrentUser();
          set({ user, isAuthenticated: true });
        } catch {
          get().logout();
        }
      },
    }),
    {
      name: TOKEN_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tokens: state.tokens,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // При rehydrate (перезагрузка страницы) persist восстанавливает tokens из
      // localStorage как есть. Если ранее (до фикса normalizeTokens в authService)
      // туда попал объект в snake_case от Keycloak, tokens.refreshToken был undefined
      // → при истёкшем access-токене refresh падал с 'No refresh token' → авторизованные
      // запросы к manage/notification давали 401 без попытки refresh. Нормализуем здесь,
      // чтобы любая сохранённая запись приводилась к camelCase независимо от источника.
      onRehydrateStorage: () => (state) => {
        if (state?.tokens) {
          state.tokens = normalizeTokens(state.tokens as unknown as Parameters<typeof normalizeTokens>[0]);
        }
      },
    },
  ),
);

// Держим tokenAccess в синхронизации с актуальными tokens из store. api.ts и
// useWebSocket читают токен через tokenAccess (а не напрямую из authStore), чтобы
// избежать цикла импортов; поэтому при любом изменении tokens (логин/рефрешш/логаут,
// в т.ч. восстановление из localStorage при старте) значение должно переезжать сюда.
useAuthStore.subscribe((state) => {
  if (state.tokens) {
    setTokens(state.tokens);
  } else {
    clearTokens();
  }
});
// Первичная инициализация: при создании модуля persist мог уже восстановить tokens
// из localStorage — прокидываем это значение в tokenAccess.
setTokens(useAuthStore.getState().tokens);

export const getAuthToken = (): string | null => {
  return useAuthStore.getState().tokens?.accessToken ?? null;
};

export const refreshAuthToken = async (retryAttempts = 3): Promise<string> => {
  for (let i = 0; i < retryAttempts; i++) {
    try {
      return await useAuthStore.getState().refreshToken();
    } catch {
      // Retry on failure
    }
  }
  throw new Error('Failed to refresh token after multiple attempts');
};

export const clearAuth = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Ignore storage errors
  }
  useAuthStore.getState().logout();
};
